import React, { useState, useEffect } from "react";
import transactionVolumeImg from "assets/images/volume-chart-img.svg";
import { XAxis, YAxis, ResponsiveContainer, BarChart, Bar } from "recharts";
import {
  Typography,
  NewDateFIlterModal as DateFilterModal,
  Select,
  DateCTA,
  MultiSelect,
  Loader,
} from "components";
import { useSelector } from "react-redux";
import {
  setTransactionStatsTypes,
  setTransactionStatsDates,
  getTransactionStatsBreakdown,
} from "redux/actions/dashboard/terminalDashboardActions";
import { getUniqueTransactionTypes, formatAmount } from "utils/helper";

const TransactionVolumeAnalytics = ({ showMenu = true }) => {
  const [openDateModal, setOpenDateModal] = useState(false);
  const [dataToDisplay, setDataToDisplay] = useState("1");

  const { transactionStatsBreakdown, transactionTypes } = useSelector(
    (state) => ({
      transactionStatsBreakdown:
        state.terminalDashboardReducer.transactionStatsBreakdown,
      transactionTypes: state.transactionsReducer.transactionTypes,
    })
  );

  const { dates, selectedTransactionTypes, data, loading } =
    transactionStatsBreakdown;

  const handleModalOpen = () => setOpenDateModal(!openDateModal);

  useEffect(() => {
    getTransactionStatsBreakdown();
  }, []);

  const newData = data.data?.details;
  const totalValue =
    dataToDisplay === "1"
      ? formatAmount(data.data?.totalVolume)
      : formatAmount(data.data?.totalValue);

  const formattedData = newData?.map((info) => {
    let infoObj = { label: info.label };

    info?.breakdown?.forEach((item) => {
      const key = Object.values(item)[0].toLowerCase();
      const value = dataToDisplay === "1" ? item.volume : item.value;

      infoObj[key] = value;
    });

    return infoObj;
  });

  const colorCodes = [
    "#8280FF",
    "#102E88",
    "#3784FB",
    "#27AE60",
    "#5270CC",
    "#E4EAFD",
  ];

  const newColorCodes = getUniqueTransactionTypes(transactionTypes).map(
    (type, i) => ({
      key: type.type.toLowerCase(),
      name: type.description,
      color: colorCodes[i],
    })
  );

  const handleModalClose = () => setOpenDateModal(false);
  const getTerminalData = () => {
    getTransactionStatsBreakdown();
    handleModalClose();
  };

  const chartWidth = 8 * formattedData?.length;

  return (
    <div className="dashboard-card terminal-value-chart-container">
      <div
        className="terminal-value-chart chart"
        style={{ minWidth: `${chartWidth}rem` }}
      >
        <div
          className="d-flex justify-content-between align-items-center pt-2 pb-3"
          style={{ maxWidth: "24rem" }}
        >
          <div>
            <Select
              required={false}
              // className={"data-type-select"}
              name="dataType"
              onChange={(e) => setDataToDisplay(e.target.value)}
              value={dataToDisplay}
              options={[
                { label: "Transaction Volume", value: "1" },
                { label: "Transaction Value", value: "2" },
              ]}
            />
            <div className="d-flex align-items-center">
              <Typography state="primary" variant="body2" className={"mt-2"}>
                {totalValue || 0}{" "}
              </Typography>{" "}
              &nbsp;
              <img
                src={transactionVolumeImg}
                height="13px"
                alt=""
                className="mt-2"
              />{" "}
            </div>
          </div>
          {showMenu && (
            <>
              <div>
                <DateCTA onClick={handleModalOpen} />
                <div className="mt-2">
                  <MultiSelect
                    placeholder={"All Transactions"}
                    // value={selectedTransactionTypes}
                    isSearchable={false}
                    clearable={false}
                    borderStyle="1px solid #102E88"
                    handleChange={(val) => setTransactionStatsTypes(val)}
                    options={getUniqueTransactionTypes(transactionTypes)?.map(
                      ({ type, description }) => ({
                        label: description,
                        value: type,
                      })
                    )}
                    // onBlur={() => getAllData()}
                    name="transaction-type-select"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {showMenu && (
          <div
            className="d-flex justify-content-end flex-wrap mb-4"
            style={{ maxWidth: "19rem" }}
          >
            {selectedTransactionTypes.length ===
              getUniqueTransactionTypes(transactionTypes)?.length ||
            !selectedTransactionTypes.length ? (
              <Typography variant="caption0">All Transactions</Typography>
            ) : (
              selectedTransactionTypes.map((transaction, i) => (
                <Typography variant="caption0" key={i}>
                  {transaction.label}
                  {i === selectedTransactionTypes.length - 1 ? "." : ","} &nbsp;
                </Typography>
              ))
            )}
          </div>
        )}

        {loading ? (
          <div
            className={"text-center"}
            style={{ marginTop: "6rem", height: "13rem" }}
          >
            <Loader />
            <Typography state="grey" variant="body2">
              Loading data...
            </Typography>
          </div>
        ) : formattedData?.length ? (
          <div
            style={{ height: "100%", minWidth: data.length > 5 ? "30rem" : "" }}
          >
            <ResponsiveContainer width="100%" height="53%">
              <BarChart
                width={730}
                height={250}
                data={formattedData}
                margin={{ left: 8, bottom: 5 }}
              >
                <XAxis
                  dataKey="label"
                  dy={15}
                  axisLine={false}
                  tickSize={0}
                  minTickGap={20}
                  style={{
                    fontSize: "10px",
                    fontWeight: 500,
                    fontFamily: "Work Sans",
                    color: "#9A9A9A",
                  }}
                />
                <YAxis
                  style={{
                    fontSize: "10px",
                    fontFamily: "Work Sans",
                    color: "#9A9A9A",
                  }}
                  tickFormatter={(val) => formatAmount(val)}
                  tickSize={0}
                  dx={-10}
                />
                {newColorCodes.map(({ key, color }, index) => (
                  <Bar
                    key={index}
                    dataKey={key}
                    fill={color}
                    barSize={4}
                    radius={[40, 40, 40, 40]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="color-codes-container d-flex justify-content-center flex-wrap px-3">
              {newColorCodes?.map(({ name, color }, key) => (
                <div className="d-flex align-items-center" key={key}>
                  <div
                    className="color-codes"
                    style={{ backgroundColor: color }}
                  ></div>{" "}
                  &nbsp;
                  <Typography state="grey" variant="caption0">
                    {name}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ height: "13rem" }}>
            <Typography
              state="grey"
              variant="body2"
              className={"text-center space-top-4"}
            >
              No information within the selected dates
            </Typography>
          </div>
        )}
      </div>
      <DateFilterModal
        dateFrom={dates.dateFrom}
        dateTo={dates.dateTo}
        openDateModal={openDateModal}
        onClose={handleModalClose}
        handleFromChange={(val) => setTransactionStatsDates(val, "dateFrom")}
        handleToChange={(val) => setTransactionStatsDates(val, "dateTo")}
        onButtonClick={getTerminalData}
      />
    </div>
  );
};

export default TransactionVolumeAnalytics;
